/**
 * Ghost Login — handles impersonation session setup.
 *
 * Flow:
 * 1. Admin tab stores ghost data in localStorage('ghost_pending')
 * 2. This page opens in a new tab
 * 3. Reads ghost_pending from localStorage, deletes it
 * 4. Sets ghost_token + ghost_user in sessionStorage (tab-scoped)
 * 5. Seeds Zustand auth-storage in sessionStorage (ghost-aware adapter reads it)
 * 6. Redirects to /dashboard
 *
 * When the tab closes, sessionStorage is cleared = ghost session gone.
 * The admin's real session in localStorage is never touched.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';

export default function GhostLoginPage() {
  const navigate = useNavigate();
  const { setTokens, checkAuth } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const raw = localStorage.getItem('ghost_pending');

    if (!raw) {
      setError('No ghost login data found. This link may have already been used.');
      return;
    }

    try {
      const { ghostToken, user, impersonatedBy } = JSON.parse(raw);

      if (!ghostToken || !user) {
        setError('Invalid ghost login data.');
        return;
      }

      // Clean up from localStorage immediately so it can't be reused
      localStorage.removeItem('ghost_pending');

      // Set ghost session markers in sessionStorage (tab-scoped)
      sessionStorage.setItem('ghost_token', ghostToken);
      sessionStorage.setItem('ghost_user', JSON.stringify(user));
      sessionStorage.setItem('ghost_impersonated_by', impersonatedBy || 'Admin');

      // Seed the Zustand store — the ghost-aware storage adapter will now
      // read/write sessionStorage instead of localStorage for this tab.
      setTokens(ghostToken, null);

      // Fetch full user profile from API and navigate to dashboard
      checkAuth().then(() => {
        navigate('/dashboard', { replace: true });
      });
    } catch {
      setError('Failed to parse ghost login data.');
    }
  }, [navigate, setTokens, checkAuth]);

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="max-w-sm text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <span className="text-xl">!</span>
          </div>
          <p className="font-medium text-red-600 dark:text-red-400">{error}</p>
          <p className="text-muted-foreground mt-2 text-sm">You can close this tab.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-purple-500 border-t-transparent" />
        <p className="mt-4 text-gray-500">Starting ghost session...</p>
      </div>
    </div>
  );
}
